/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-11-28
 */

import { ChangeEvent, useEffect, useState } from "react";
import { Button, Card, CardBody, Container, Modal } from "react-bootstrap";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, X } from "lucide-react";
import { CastType, IQuery, IWhere } from "./Interfaces";
import { useAuth } from "../../contexts/AuthContext";
import { formatDateFull } from "./utils/AppUtil";
import { JsonToHtmlTable } from "./utils/JsonObjToTableUtil";
import { useLocation, useNavigate } from 'react-router-dom';

interface INotification {
    
    "user.id": string | null,
    "user.user_name": string | null,
    "user.first_name": string | null,
    "user.last_name": string | null,
    "user.email": string | null,
    "user.cellphone": string | null,
    "user.created": string | null,
    "user.updated": string | null,

    id: string,
    repeat_count: number,
    reference: string | null,
    category: string | null,
    system: string | null,
    description: string | null,
    function_code: string | null,
    handled: boolean,
    handled_at: string | null,
    handled_comment: string | null,
    last_encountered: string | null,
    created: string | null,
    updated: string | null,
}

const notificationQuery: IQuery = {
    tableName: "dst_notification",
    select: [

    ],
    groupBy: [],
    join: [
        {
            "relationship": "user",
            "joinType": "LEFT"
        },
        {
            "relationship": "function",
            "joinType": "LEFT"
        }                
    ],
    orderBy: [ 
        {
            fullColumnName: "last_encountered",
            direction: "DESC"
            
        }
    ],
    where: [
        {
            "fullColumnName": "handled",
            "value": false,
            "operator": "EQ",
            "cast": CastType.BOOLEAN
        }, 
        {
            "fullColumnName": "infrastructure",
            "value": false,
            "operator": "EQ",
            "cast": CastType.BOOLEAN
        }               
    ],
    mainTableOnly: false,
    includeExtraColumns: true,
    includeControlColumns: true,
    modifiedAfter: null,
    touchedBefore: null,
    isDirty: null,
    pageNum: 0,
    pageSize: 100,
    frontEndOnly: null
} 

const repeatCountBgColour = (count: number): string => {

    if(count < 5) {
        return 'bg-primary'; 
    }

    if(count >= 5 && count < 30) {
        return 'bg-warning'; 
    }

    if(count >= 30) {
        return 'bg-danger'; 
    }

    return 'bg-primary';
}



export const Notification: React.FC = () => {

    const location = useLocation();
    const navigate = useNavigate(); 

    const [shipmentReference, setShipmentReference] = useState<string>(location.state?.shipment_num ?? "");
    const [errorCategory, setErrorCategory] = useState<string>(location.state?.category ?? "");

    const [notificationDescription, setNotificationDescription] = useState<string>(location.state?.description ?? "");
    const [notificationLastEncountered, setNotificationLastEncountered] = useState<string>(location.state?.last_encountered ?? "");

    const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
    const { fetchData }  = useAuth();
    
    const [queryState, setQueryState] = useState<IQuery>(notificationQuery);
    const [notifications, setNotifications] = useState<INotification[]>([]);

    const [searchState, setSearchState] = useState<{ [key: string]: string }>({
        reference: "",
        category: "",
        system: "",
        function_code: "",
        description: "",
        created: "",
        last_encountered: "",
    });

    const [tableRows, setTableRows] = useState<React.ReactElement[]>([]);

    // Keep track of the currentlly selected record.
    const [currentRecordIndex, setCurrentRecordIndex] = useState<number>(0);

    // Handled?
    const [isHandled, setIsHandled] = useState(false);
    const [showSystemNotifications, setShowSystemNotifications] = useState<boolean>(false);
    // Modal Show & Close.
    const [showModal, setShowModal] = useState(false);

    const [hasGotShipmentReference, setHasGotShipmentReference] = useState<boolean>(false);
    const [isNotificationsFetched, setIsNotificationsFetched] = useState<boolean>(false);
    const [hasUsedInitialReference, setHasUsedInitialReference] = useState<boolean>(false);

    //Pagination Testing 
    const [pageNumber, setPageNumber] = useState<number>(0); 
    const [pageSize, setPageSize] = useState<number>(100);   
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    
    const [blockInitialFetch, setBlockInitialFetch] = useState<boolean>(!!location.state);

    // Default search state generator
    function defaultSearchState() {
        return {
            reference: "",
            category: "",
            system: "",
            function_code: "",
            description: "",
            created: "",
            last_encountered: "",
        };
    }

    // Set searchState from initial location.state
    useEffect(() => {
        if (!hasUsedInitialReference) {
         const timer = setTimeout(() => {
            if (shipmentReference) {
                // Case 1 or 2: Shipment reference (with or without category)
                setSearchState(prev => ({
                    ...prev,
                    reference: shipmentReference,
                    category: errorCategory,
                }));
            } else if (errorCategory && notificationDescription && notificationLastEncountered) {
                // Case 3: Category + Description + Last Encountered
                setSearchState(prev => ({
                    ...prev,
                    category: errorCategory,
                    description: notificationDescription,
                    last_encountered: notificationLastEncountered,
                }));
            }
            setHasGotShipmentReference(true);
            setHasUsedInitialReference(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }, [ shipmentReference, errorCategory, notificationDescription, notificationLastEncountered, hasUsedInitialReference]); 


   useEffect(() => {
        if (!hasGotShipmentReference) return;

        const where: IWhere[] = [
            {
                fullColumnName: "handled",
                value: isHandled,
                operator: "EQ"
                
            }
        ];

        if (shipmentReference) {
            where.push({
                fullColumnName: "reference",
                value: `%${shipmentReference}%`,
                operator: "LIKE",
                cast: CastType.STRING
            });

            if (errorCategory) {
                where.push({
                    fullColumnName: "category",
                    value: `%${errorCategory}%`,
                    operator: "LIKE",
                    cast: CastType.STRING
                });
            }
        } else if (errorCategory && notificationDescription && notificationLastEncountered) {
            where.push(
                {
                    fullColumnName: "category",
                    value: `%${errorCategory}%`,
                    operator: "LIKE",
                    cast: CastType.STRING
                },
                {
                    fullColumnName: "description",
                    value: `%${notificationDescription}%`,
                    operator: "LIKE",
                    cast: CastType.STRING
                },
                {
                    fullColumnName: "last_encountered",
                    value:`%${notificationLastEncountered}%`,
                    operator: "LIKE",
                    cast: CastType.TIMESTAMP
                }
            );
        }

        // Update query and unblock fetch
        setQueryState(prev => ({ ...prev, where }));
        setBlockInitialFetch(false);

        // Clear used filters
        setShipmentReference("");
        setErrorCategory("");
        setNotificationDescription("");
        setNotificationLastEncountered("");

    }, [hasGotShipmentReference, isHandled]);


    useEffect(() => {
        //console.log("queryState updated", queryState);
        if (blockInitialFetch) {
            //console.log("Blocked initial fetch");
            return;
        }

        //console.log("Fetching notifications");
        fetchNotifications();
    }, [queryState, blockInitialFetch]);

    useEffect(() => {
    const hasRouteState =
            location.state?.shipment_num ||
            (location.state?.category && location.state?.description && location.state?.last_encountered);

        if (!hasRouteState && !hasUsedInitialReference) {
            setSearchState(defaultSearchState());
            setQueryState(notificationQuery);
            setHasGotShipmentReference(false);
            setHasUsedInitialReference(true);
        }
    }, [location.state, hasUsedInitialReference]);

    // Clear location.state after use
    useEffect(() => {
        if (hasUsedInitialReference) {
            navigate(location.pathname, { replace: true });
        }
    }, [hasUsedInitialReference, location.pathname, navigate]);

    // Pagination updates
    useEffect(() => {
        setQueryState(prev => ({
            ...prev,
            pageNum: pageNumber,
            pageSize: pageSize,
        }));
    }, [pageNumber, pageSize]);

    
    
    // Update the QueryState when the handled checkbox is changed
    const handleQueryHandledChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        const query = {...queryState}
        query.where.forEach((clause: IWhere, index) => {
            if(clause.fullColumnName === "handled"){
                setIsHandled(event.target.checked);
                clause.value = event.target.checked;
            }
        });
        setQueryState(query); 
    };

     // Update the QueryState when the show system notifications checkbox is changed
     const handleQuerySystemNotificationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        const query = {...queryState}
        query.where.forEach((clause: IWhere, index) => {
            if(clause.fullColumnName === "infrastructure"){
                setShowSystemNotifications(event.target.checked);
                clause.value = event.target.checked;
            }
        });
        setQueryState(query); 
    };

    // Set the current record and show the record detail modal.
    const showDetail = (index: number) => {
        setCurrentRecordIndex(index);
        setShowModal(true);
    }
    
    // Build the Notification Table
    const buildTableRows = (data: any[]): React.ReactElement[] => {
        const rowElements: React.ReactElement[] = [];
    
        const cellStyle: React.CSSProperties = { border: '1px solid #deebed', whiteSpace: 'nowrap', padding: '5px 8px', fontSize: '0.75rem' };
    
        data.map((record, index) => {
            const colour = repeatCountBgColour(record.repeat_count);
    
            rowElements.push(
                <tr id={`${index}`} key={index}>
                    <td style={cellStyle} className={`${colour} text-white text-center`} title={record?.repeat_count}>{record?.repeat_count}</td>
                    <td style={{ ...cellStyle, cursor: "pointer", textDecoration: "underline" }} onClick={() => showDetail(index)}  title={record?.reference}>
                        {record?.reference || "-"}
                    </td>
                    <td style={cellStyle} title={record?.category} >{record?.category}</td>
                    <td style={cellStyle} title={record?.system}>{record?.system}</td>
                    <td style={cellStyle} title={record?.function_code}>{record?.function_code}</td>
                    <td style={{ ...cellStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={record?.description}>
                    {record?.description}
                    </td>
                    <td style={cellStyle} title={record?.created}>{formatDateFull(record?.created)}</td>
                    <td style={cellStyle} title={record?.last_encountered}>{formatDateFull(record?.last_encountered)}</td>
    
                    <td style={cellStyle}>
                        {isHandled && (
                            <table className="table table-borderless">
                                <tbody>
                                    <tr>
                                        <td><b>Handled By:</b></td>
                                        <td>{record["user.user_name"]}</td>
                                    </tr>
                                    <tr>
                                        <td>{record["user.first_name"]}</td>
                                        <td>{record["user.last_name"]}</td>
                                    </tr>
                                    <tr>
                                        <td><b>Handled at:</b></td>
                                        <td>{formatDateFull(record["handled_at"])}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>{record["handled_comment"]}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
    
                        {!isHandled && (
                            <form
                                id={`${record?.id}`}
                                name={`${record?.id}`}
                                className="d-flex align-items-center"
                                method="POST"
                                action={`${process.env.REACT_APP_BASE_URL}/notifications/${record.id}/handle`}
                                onSubmit={(e) => e.preventDefault()}
                            >
                                <textarea
                                    id={`handled_comment-${record?.id}`}
                                    name={`handled_comment-${record?.id}`}
                                    className="form-control me-2"
                                    rows={1}
                                    placeholder="Comment..."
                                    style={{ width: '140px'}}
                                ></textarea>
    
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        const form = document.getElementById(`${record?.id}`) as HTMLFormElement | null;
                                        const textArea = document.getElementById(`handled_comment-${record?.id}`) as HTMLFormElement | null;
    
                                        if (form) {
                                            const formData = new FormData(form);
                                            const comment = formData.get(`handled_comment-${record?.id}`);
                                            fetchData(form.action, { method: "POST", body: JSON.stringify({ handledComment: comment }) }, fetchNotifications);
                                        }
    
                                        if (textArea) {
                                            textArea.value = "";
                                        }
                                    }}
                                >
                                    Handle
                                </button>
                            </form>
                        )}
                    </td>
                </tr>
            );
        });
    
        return rowElements;
    };
    

    const processFetch = (result: any) => {
        
        const data: INotification[] = [];

        for(const key in result.data){
            data.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
            //console.log({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
        }

        setNotifications(data);
        setIsNotificationsFetched(true);
        setTableRows(buildTableRows(data));
      
        // Pagination 
        setPageSize(pageSize);
        setTotalRecords(result?.numRecords);
        setTotalPages(Math.ceil(result?.numRecords / pageSize));
    }

    const fetchNotifications = () => {
        setNotifications([]);
        fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processFetch);
    }

    const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value } = event.target;
        //console.log(`${id} : ${value}`);
        setSearchState((prev) => ({ ...prev, [id]: value }));
    };

    // Function to handle date parsing and formatting
    function parseDate(value: string): string {
        let date: Date | null = null;

        // Try to parse the value in various formats
        const datePatterns = [
            /^\d{2}-\d{2}-\d{4}$/, // dd-mm-yyyy
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2} [APM]{2}$/, // dd-mm-yyyy hh:mm:ss AM/PM
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/, // dd-mm-yyyy hh:mm:ss
            /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd (valid input)
        ];

        // Check if the value matches any of the patterns
        if (datePatterns[0].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [day, month, year] = value.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[1].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' '); 
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[2].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' ');
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[3].test(value)) {
            // Valid yyyy-mm-dd, no changes needed
            date = new Date(value);
        } else {
            // If no pattern matches, return the original value
            return value;
        }

        // If date is valid, format it as yyyy-mm-dd
        if (date && !isNaN(date.getTime())) {
            return date.toISOString().split('T')[0]; // Format as yyyy-mm-dd
        }

        // If invalid date, return the original value
        return value;
    }

    const searchClicked = () => {
        let where: IWhere[] = [];

        // Always include handled and infrastructure
        where.push(
            {
                fullColumnName: "handled",
                value: isHandled,
                operator: "EQ"
               
            },
            {
                fullColumnName: "infrastructure",
                value: showSystemNotifications,
                operator: "EQ"
               
            }
        );

        Object.entries(searchState).forEach(([key, value]) => {
            const trimmedValue = value.trim(); 

            if (key === "created" || key === "last_encountered") {
                if (trimmedValue) {
                    const formattedValue = parseDate(trimmedValue);
                    value = formattedValue;
                }
            }

            if (trimmedValue.length) {
                const clause: IWhere = {
                    fullColumnName: key,
                    value: '%' + trimmedValue + '%',
                    operator: "LIKE",
                    cast: CastType.STRING
                };
                where.push(clause);
            }
        });

        const query = { ...queryState };
        query.where = where;
        setQueryState(query);
        setPageNumber(0); // Reset page to first page
        setPageSize(100); // Reset page size to 100
    };

    const clearClicked = () => {

        setSearchState({
            reference: "",
            category: "",
            system: "",
            function_code: "",
            description: "",
            created: "",
            last_encountered: "",
        });

        let where: IWhere[] = [];
        where.push(
            {
                "fullColumnName": "handled",
                "value": isHandled,
                "operator": "EQ"
                
            },
            {
                "fullColumnName": "infrastructure",
                "value": showSystemNotifications,
                "operator": "EQ"
               
            }            
        ); 
        const query = {...queryState}
        query.where = where;
        setQueryState(query);
    }    


 
    // Pagination handlers
    const goToFirstPage = () => setPageNumber(0);
    const goToPreviousPage = () => setPageNumber(prev => prev - 1);
    const goToNextPage = () => setPageNumber(prev => prev + 1);
    const goToLastPage = () => setPageNumber(totalPages - 1);

    // Update page size handler
    const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
        setPageNumber(0); // Reset to first page when changing page size
    };


    const thStyle = {
         // textWrap: 'wrap',
         backgroundColor: '#293042',
         border: '1px solid #deebed',
         fontWeight: 600,
         color:'white',
         // height: '50px',
         padding: '12px 8px', fontSize: '0.75rem'
    };

    const thControlsStyle = {
        textWrap: 'nowrap',
        backgroundColor: 'white', 
        border: '1px solid #deebed',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding:'2px 0px',
        margin: '0px'

    };

    const thSearchStyle = {
        // textWrap: 'nowrap',
        // backgroundColor: 'white', 
        // border: '1px solid #deebed',
        // padding: '5px 4px'
        textWrap: 'nowrap',
        backgroundColor: 'white', //lightgray //white
        border: '1px solid #deebed',
        padding: '8px 4px'
    };


    return (
        <Container fluid className="p-0">
            <Card>
                <Card.Header>
                    <Card.Title>{"Error Notifications"}</Card.Title>               
                </Card.Header>
                <CardBody>
                  { /* Need to build a generic pagination Start */ }   
                       <div className="d-flex flex-wrap align-items-center justify-content-between m-2">
                            <div className="d-flex flex-row gap-2 mt-3">
                                <h6 className="card-subtitle text-muted">Total Records: {totalRecords}</h6>
                                <h6 className="card-subtitle text-muted">Current Page : [ { pageNumber + 1 } of {totalPages} ]</h6>
                            </div>

                            <div className="d-flex align-items-center gap-3">
                                {/* // Page Size */}
                                <nav aria-label="">
                                    <ul className="pagination m-0">
                                        <li className="page-item">
                                            <select className="page-link" 
                                                value={pageSize}
                                                onChange={handlePageSizeChange}
                                                style={{width: '100px', textAlign: 'center', padding: '10px'}}>
                                                <option defaultChecked value={pageSize}>{pageSize}</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={75}>75</option>
                                                <option value={100}>100</option>
                                                <option value={150}>150</option>
                                                <option value={200}>200</option>
                                                <option value={250}>250</option>
                                                <option value={500}>500</option>
                                                <option value={750}>750</option>
                                                <option value={1000}>1000</option>
                                            </select>
                                        </li>                            
                                    </ul>
                                </nav>

                                {/* // Pagination Control */}
                                <nav aria-label="">
                                    <ul className="pagination m-0">
                                    <li className={`page-item ${pageNumber === 0 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center gap-1"
                                        onClick={goToFirstPage}
                                        disabled={pageNumber === 0}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronsLeft size={18} />
                                        </button>
                                    </li>

                                    <li className={`page-item ${pageNumber <= 0 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center"
                                        onClick={goToPreviousPage}
                                        disabled={pageNumber <= 0}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronLeft size={18} />
                                        </button>
                                    </li>

                                    <li className="page-item">
                                        <span
                                        className="page-link d-flex align-items-center justify-content-center"
                                        style={{ height: '38px', marginTop: '1px', padding: '0 14px' }}
                                        >
                                        Page {pageNumber + 1} of {totalPages}
                                        </span>
                                    </li>

                                    <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center"
                                        onClick={goToNextPage}
                                        disabled={pageNumber >= totalPages - 1}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronRight size={18} />
                                        </button>
                                    </li>

                                    <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center gap-1"
                                        onClick={goToLastPage}
                                        disabled={pageNumber >= totalPages - 1}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronsRight size={18} />
                                        </button>
                                    </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>  
               { /* Need to build a generic pagination END */ }  
               <div className="sa-table-container" >
                <table className="table table-bordered sa-table">
                    <>
                     <thead className="sa-list-search">
                     <tr>
                            <td className="sa-search-control" style={thSearchStyle}></td>                                    
                            <td className="sa-search-control" style={thSearchStyle}>
                                <input  id={`reference`}
                                        name="reference" 
                                        value={searchState.reference} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Referene...`} 
                                        className="form-control"
                                        >
                                </input>
                            </td>                            
                            <td className="sa-search-control" style={thSearchStyle}>
                                <input  id={`category`}
                                        name="category" 
                                        value={searchState.category} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Category...`} 
                                        className="form-control"
                                        >
                                </input>
                            </td>
                            <td className="sa-search-control" style={thSearchStyle}>
                                <input  id="system"
                                        name="system" 
                                        value={searchState.system} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search System...`} 
                                        className="form-control"
                                        >
                                </input>
                            </td>
                            <td className="sa-search-control" style={thSearchStyle}>
                                <input  id="function_code"
                                        name="function_code" 
                                        value={searchState.function_code} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Function Code...`} 
                                        className="form-control"
                                       >
                                </input>
                            </td>
                            <td className="sa-search-control" style={thSearchStyle}>
                                <input  id="description"
                                        name="description" 
                                        value={searchState.description} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Description...`} 
                                        className="form-control">
                                </input>
                            </td>
                            <td className="sa-search-control" style={thSearchStyle}>
                                <input  id="created"
                                        name="created" 
                                        value={searchState.created} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Created...`} 
                                        className="form-control">
                                </input>
                            </td>
                            <td  className="sa-search-control" style={thSearchStyle}>

                                <input  id={`last_encountered`}
                                        name="last_encountered" 
                                        value={searchState.last_encountered} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Last Encountered...`} 
                                        className="form-control"
                                        >
                                </input>
                            </td>
                            <td colSpan={2} style={thControlsStyle}  className="sa-search-control">
                                {/* <Search color="#c84338" onClick={searchClicked} className="custom-icon text-center align-middle" />
                                <X color="#c84338" onClick={clearClicked} style={{width: "36px", height: "36px"}} 
                                className="custom-icon text-center align-middle ms-5"/> */}
                                <button onClick={searchClicked} type="button" style={{border:'1px solid rgb(222, 235, 237)', backgroundColor: 'white', borderRadius: '5px', margin: '1px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#495057" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="" style={{width: '34px', height: '34px', margin: "0px", padding:"2px"}}>
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="m21 21-4.3-4.3"></path>
                                </svg>
                                </button >   
                                <button onClick={clearClicked} type="button" style={{ border:'1px solid rgb(222, 235, 237)', backgroundColor: 'white', borderRadius: '5px', margin: '1px' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#495057" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="" style={{width: '34px', height: '34px', margin: "0px"}}>
                                        <path d="M18 6 6 18"></path>
                                        <path d="m6 6 12 12"></path>
                                    </svg>
                                </button> 
                            </td>
                        </tr> 
                     </thead>
                    </>

                    <>
                    <thead className="sa-list-columns">
                    <tr>
                            <td style={thStyle}>Occurrences</td>
                            <td style={thStyle}>Reference</td>
                            <td style={thStyle}>Category</td>
                            <td style={thStyle}>System</td>
                            <td style={thStyle}>FunctionCode</td>
                            <td style={thStyle}>Description</td>
                            <td style={thStyle}>Created</td>
                            <td style={thStyle}>Last Encountered</td>
                            <td style={thStyle}>
                                <div className="form-check me-2">
                                <>
                                    <div>
                                        <input
                                            type="checkbox"
                                            id="handled"
                                            name="handled"
                                            className="form-check-input"
                                            checked={isHandled}
                                            onChange={handleQueryHandledChange}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="handled"
                                        >
                                        Handled
                                        </label>
                                    </div>
                                    <div>
                                        <input
                                            type="checkbox"
                                            id="infrastructure"
                                            name="infrastructure"
                                            className="form-check-input"
                                            checked={showSystemNotifications} // Make sure you update this if needed.
                                            onChange={handleQuerySystemNotificationsChange}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="infrastructure"
                                        >
                                        System Notifications
                                        </label>
                                    </div>
                                    </>
                                </div>                                                                     
                            </td>
                        </tr> 
                    </thead>
                    </>
                    <tbody>
                        {tableRows}
                    </tbody>
                </table>  
                </div>
                </CardBody>
            </Card>

            {/* Modal structure */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                <Modal.Title>Doc Num: {notifications[currentRecordIndex]?.reference ? notifications[currentRecordIndex]?.reference : "N/A"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <JsonToHtmlTable jsonObj={notifications[currentRecordIndex]} />
                </Modal.Body>
                <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                    Close
                </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
}